import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ArrowRight } from "lucide-react";

import { useLocationName } from "@/api/locations";
import {
  useNeededFor,
  useNodeOrders,
  type PlannedOrder,
} from "@/api/planningRun";
import { useRunProjection, type NodeDraw } from "@/api/stockChart";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDate, formatNumber } from "@/utils/formatters";

import { OrderKind, OrderSource } from "./OrderKind";
import { STATUS_COLOR } from "./OrderStatus";

type Entry = { date: string } & ({ order: PlannedOrder } | { draw: NodeDraw });

const quantity = (q: number | string) =>
  formatNumber(Number(q), { maximumFractionDigits: 4 });

/** One node in the run along a timeline: its orders arriving on the left,
 *  the orders drawing on it on the right. With a selected order, the draws
 *  it's needed for stand out. */
export function NodeOrders({
  runId,
  itemId,
  locationId,
  selectedOrderId,
}: {
  runId: number;
  itemId: number;
  locationId: number;
  selectedOrderId?: number;
}) {
  const { t } = useTranslation();
  const orders = useNodeOrders(runId, itemId, locationId);
  const projection = useRunProjection(runId, itemId, locationId);
  const pegs = useNeededFor(runId, selectedOrderId ?? 0, {
    enabled: selectedOrderId != null,
  });
  const plan = projection.data?.plan;

  const entries = useMemo(
    (): Entry[] =>
      [
        ...(orders.data?.results ?? []).map((order) => ({
          date: order.dueDate,
          order,
        })),
        ...(plan?.draws ?? []).map((draw) => ({ date: draw.date, draw })),
      ].sort(
        (a, b) =>
          a.date.localeCompare(b.date) ||
          Number("draw" in a) - Number("draw" in b),
      ),
    [orders.data, plan],
  );

  const pegged = new Set(pegs.data?.map((p) => p.plannedOrder));
  const selected = entries.find(
    (e) => "order" in e && e.order.id === selectedOrderId,
  );
  const lastPegged = [...entries]
    .reverse()
    .find((e) => "draw" in e && pegged.has(e.draw.plannedOrder));
  const inWindow = (date: string) =>
    !!selected &&
    !!lastPegged &&
    selected.date <= date &&
    date <= lastPegged.date;

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">
        {t("planningReview.nodeOrders", "Orders at this location")}
      </h3>
      {orders.isLoading || projection.isLoading ? (
        <Skeleton className="h-20" />
      ) : entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("planningReview.empty", "No orders")}
        </p>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)_5rem_minmax(0,1fr)] text-sm">
          <span className="text-muted-foreground pb-1 text-right text-xs">
            {t("planningReview.ledger.incoming", "Arriving")}
          </span>
          <span />
          <span className="text-muted-foreground pb-1 text-xs">
            {t("planningReview.ledger.drawn", "Drawn by")}
          </span>
          {entries.map((e, i) => (
            <LedgerRow
              key={
                "order" in e ? `o${e.order.id}` : `d${e.draw.plannedOrder}-${i}`
              }
              entry={e}
              locationId={locationId}
              firstOfDay={entries[i - 1]?.date !== e.date}
              selectedOrderId={selectedOrderId}
              inWindow={inWindow(e.date)}
              pegged={
                "draw" in e && pegs.data
                  ? pegged.has(e.draw.plannedOrder)
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

function LedgerRow({
  entry: e,
  locationId,
  firstOfDay,
  selectedOrderId,
  inWindow,
  pegged,
}: {
  entry: Entry;
  locationId: number;
  firstOfDay: boolean;
  selectedOrderId?: number;
  inWindow: boolean;
  /** Whether the selected order is needed for this draw; absent without one. */
  pegged?: boolean;
}) {
  const locationName = useLocationName();
  // A transfer moves this item and most draws are made here: name only what differs.
  const drawTarget = (draw: NodeDraw) =>
    [
      draw.flavor !== "transferred" && `${draw.itemSku} · ${draw.itemName}`,
      draw.location !== locationId && locationName(draw.location),
    ]
      .filter(Boolean)
      .join(" · ");
  return (
    <>
      <div className="flex min-w-0 items-center justify-end gap-1 py-1">
        {"order" in e && (
          <>
            <Link
              to={`?order=${e.order.id}`}
              aria-current={e.order.id === selectedOrderId || undefined}
              className={cn(
                "hover:bg-muted/50 aria-[current]:ring-primary flex min-w-0 items-center gap-2 rounded-md border border-l-4 px-2 py-1 aria-[current]:ring-2",
                STATUS_COLOR[e.order.status].arriving,
                e.order.status === "cancelled" && "opacity-50",
              )}
            >
              <span className="text-muted-foreground min-w-0 text-xs">
                <OrderSource order={e.order} />
              </span>
              <span
                className={cn(
                  "font-semibold tabular-nums",
                  e.order.status === "cancelled" && "line-through",
                )}
              >
                +{quantity(e.order.quantity)}
              </span>
            </Link>
            <ArrowRight
              className={cn(
                "size-4 shrink-0",
                STATUS_COLOR[e.order.status].text,
              )}
            />
          </>
        )}
      </div>
      <div className="relative flex items-center justify-center gap-1 py-1">
        <div
          className={cn(
            "absolute inset-y-0 left-1/2 w-1 -translate-x-1/2",
            inWindow ? "bg-primary" : "bg-border",
          )}
        />
        {firstOfDay && (
          <time
            dateTime={e.date}
            className="bg-background relative rounded-full border px-1.5 text-xs tabular-nums"
          >
            {formatDate(e.date, { day: "numeric", month: "short" })}
          </time>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-1 py-1">
        {"draw" in e && (
          <>
            <ArrowRight className="size-4 shrink-0 text-slate-400" />
            <Link
              to={`?order=${e.draw.plannedOrder}`}
              title={`${e.draw.itemSku} · ${e.draw.itemName} · ${locationName(e.draw.location)}`}
              data-pegged={pegged}
              className={cn(
                "hover:bg-muted/50 flex min-w-0 items-center gap-2 rounded-md border border-r-4 border-dashed px-2 py-1",
                STATUS_COLOR[e.draw.status].drawing,
                pegged === false && "opacity-35",
                pegged && "ring-primary border-solid ring-2",
              )}
            >
              <span className="font-semibold tabular-nums">
                −{quantity(e.draw.quantity)}
              </span>
              <span className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs">
                <OrderKind flavor={e.draw.flavor} />
                {drawTarget(e.draw) && (
                  <span className="truncate">· {drawTarget(e.draw)}</span>
                )}
              </span>
            </Link>
          </>
        )}
      </div>
    </>
  );
}
